import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { signUp } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await signUp(email, password);
      toast.success(
        'Registration successful! 🎉 You will receive important updates about your Valentine pages via email.',
        { duration: 6000 }
      );
      navigate('/');
    } catch (error: any) {
      if (error?.message?.toLowerCase().includes('user already registered')) {
        toast.error(
          'This email is already registered. Please sign in instead.',
          { duration: 5000 }
        );
      } else if (error?.message?.toLowerCase().includes('weak password')) {
        toast.error(
          'Please choose a stronger password. It should be at least 6 characters long and include a mix of letters and numbers.',
          { duration: 5000 }
        );
      } else if (error?.message?.toLowerCase().includes('invalid email')) {
        toast.error(
          'Please enter a valid email address. You will need this email to receive important updates.',
          { duration: 5000 }
        );
      } else if (error?.message?.toLowerCase().includes('rate limit')) {
        toast.error(
          'Too many registration attempts. Please try again in a few minutes.',
          { duration: 5000 }
        );
      } else if (error?.message?.toLowerCase().includes('network')) {
        toast.error(
          'Network error. Please check your internet connection and try again.',
          { duration: 5000 }
        );
      } else {
        toast.error('Registration failed. Please try again later.', {
          duration: 5000,
        });
      }
      console.error('Registration error:', error);
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen px-4">
      <div className="max-w-md w-full bg-white p-6 rounded-lg shadow-lg">
        <h1 className="text-3xl font-bold text-center mb-6">Create Account</h1>

        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
          <p className="text-blue-700">
            <strong>Important:</strong> Please provide a valid email address.
            You'll receive:
            <ul className="list-disc ml-5 mt-2">
              <li>Valentine page approval notifications</li>
              <li>Updates when your Valentines are viewed</li>
              <li>Important service announcements</li>
              <li>Security alerts and account updates</li>
            </ul>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
              required
              placeholder="Enter a valid email address"
            />
          </div>
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
              required
              minLength={6}
            />
            <p className="mt-1 text-sm text-gray-500">
              Password must be at least 6 characters long
            </p>
          </div>
          <button
            type="submit"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
          >
            Create Account
          </button>
          <p className="text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-pink-600 hover:text-pink-700">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
